import { Button } from "@octopusdeploy/design-system-components";
import type { GitRefResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { isDeploymentProcessIdentifier, isRunbookProcessIdentifier, isTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import { PermissionCheck } from "~/components/PermissionCheck";
import { processScopedEditPermission } from "../Process/Common/CommonProcessHelpers";
import { useProcessContext } from "../Process/Contexts/ProcessContext";
type AddRunbookStepButtonProps = {
    spaceId: string;
    projectId: string;
    processId: string;
    runbookId: string;
    projectSlug: string;
};
const AddRunbookStepButton: React.FC<AddRunbookStepButtonProps> = ({ spaceId, projectId, projectSlug, processId, runbookId }) => {
    const href = links.runbookProcessEditorPage.generateUrl({ spaceId, projectSlug, runbookId, processId }, { stepTemplates: true });
    const processContext = useProcessContext();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    return (<PermissionCheck permission={processScopedEditPermission(processContext.selectors.getProcessType())} project={projectId} wildcard={true}>
            <Button importance="secondary" label={"Add Step"} href={href} onClick={() => dispatchAction("Add Deployment Step", { resource: "Runbook Process", action: Action.Add })}/>
        </PermissionCheck>);
};
AddRunbookStepButton.displayName = "AddRunbookStepButton"
type AddDeploymentsStepButtonProps = {
    spaceId: string;
    projectId: string;
    projectSlug: string;
    gitRef: GitRefResource | undefined;
};
const AddDeploymentsStepButton: React.FC<AddDeploymentsStepButtonProps> = ({ spaceId, projectId, projectSlug, gitRef }) => {
    const href = getAddProcessStepUrl({ spaceId, projectSlug, gitRef });
    const processContext = useProcessContext();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    return (<PermissionCheck permission={processScopedEditPermission(processContext.selectors.getProcessType())} project={projectId} wildcard={true}>
            <Button importance="secondary" label={"Add Step"} href={href} onClick={() => dispatchAction("Add Deployment Step", { resource: "Deployment Process", action: Action.Add })}/>
        </PermissionCheck>);
};
AddDeploymentsStepButton.displayName = "AddDeploymentsStepButton"
interface GetProcessStepTemplatesUrlOptions {
    spaceId: string;
    projectSlug: string;
    gitRef: GitRefResource | undefined;
}
function getAddProcessStepUrl({ spaceId, projectSlug, gitRef }: GetProcessStepTemplatesUrlOptions) {
    if (gitRef) {
        return links.branchDeploymentProcessEditorPage.generateUrl({ spaceId, projectSlug, branchName: gitRef.CanonicalName }, { stepTemplates: true });
    }
    return links.deploymentProcessEditorPage.generateUrl({ spaceId, projectSlug }, { stepTemplates: true });
}
type ContextAddStepNavigationButtonProps = {
    gitRefResource: GitRefResource | undefined;
};
const ContextAddStepNavigationButton: React.FC<ContextAddStepNavigationButtonProps> = ({ gitRefResource }) => {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    if (isDeploymentProcessIdentifier(processIdentifier)) {
        return <AddDeploymentsStepButton spaceId={processIdentifier.spaceId} projectSlug={processIdentifier.projectSlug} projectId={processIdentifier.projectId} gitRef={gitRefResource}/>;
    }
    if (isRunbookProcessIdentifier(processIdentifier)) {
        return <AddRunbookStepButton spaceId={processIdentifier.spaceId} projectSlug={processIdentifier.projectSlug} projectId={processIdentifier.projectId} processId={processIdentifier.id} runbookId={processIdentifier.runbookId}/>;
    }
    if (isTemplatedProjectProcessIdentifier(processIdentifier)) {
        return <AddDeploymentsStepButton spaceId={processIdentifier.spaceId} projectSlug={processIdentifier.projectSlug} projectId={processIdentifier.projectId} gitRef={gitRefResource}/>;
    }
    throw new Error(`Unhandled process identifier ${processIdentifier}`);
};
ContextAddStepNavigationButton.displayName = "ContextAddStepNavigationButton"
export default ContextAddStepNavigationButton;
